import { WorkbookInstance } from "@fortune-sheet/react"

/**
 * The viewer's one highlight mechanism (Concept "Excel declarations and cell
 * evidence", Part VII — "The mechanism: a first-class range decoration layer").
 *
 * Two layers ride on it and never on each other: search is many small yellow
 * decorations without labels, proofs are per-statement tints with optional
 * badges. Both are composed by their caller and handed over whole — the layer
 * has set semantics, so there is no keying or removal bookkeeping anywhere.
 *
 * A decoration is described in *workbook* coordinates — a sheet and a
 * rectangular cell range — never in renderer terms. That is what lets the
 * renderer be replaced (the named exit is Univer) without touching any caller.
 * `WorkbookInstance` is the only renderer type in this file, and it stops here:
 * nothing above the viewer boundary handles it.
 */
export type WorkbookRange = {
  /** 0-based, inclusive. */
  startRow: number
  /** 0-based, inclusive. */
  endRow: number
  /** 0-based, inclusive. */
  startColumn: number
  /** 0-based, inclusive. */
  endColumn: number
}

export type RangeDecoration = {
  /** Caller-owned identity; also emitted as `data-decoration-id`. */
  id: string
  /**
   * Sheet *name*, as the workbook itself spells it — not an ordinal and not a
   * renderer sheet id. Names are unique per workbook by the file format, they
   * are what a reviewer reads, and they are what the persisted surface catalog
   * maps its ordinals to; an ordinal here would additionally have to agree on
   * hidden-sheet handling and on a base, which is exactly the ambiguity the
   * catalog exists to remove.
   */
  sheet: string
  range: WorkbookRange
  /** CSS colour for the block's fill. Carry the translucency in the colour. */
  fill: string
  /** CSS colour for a 1px border around the block. */
  border?: string
  /** Short label pinned just outside the block's top-left corner. */
  badge?: string
  /** Extra class on the block, for caller-owned styling (e.g. active hit). */
  className?: string
}

/**
 * Replace the whole decoration layer. Passing `[]` clears it.
 *
 * Non-mutating by construction: the layer lives on the renderer's context, not
 * on sheet data, so it produces no undo-history entry, no change event, and
 * never touches the workbook's own fills.
 */
export const applyRangeDecorations = (
  workbook: WorkbookInstance | null | undefined,
  decorations: RangeDecoration[],
): void => {
  workbook?.setRangeDecorations(decorations)
}
